import { lazy, type ComponentType } from 'react';

const RELOAD_FLAG = 'cm:chunk-reload-attempted';

/**
 * sessionStorage throws in some privacy modes, so every access is guarded. A
 * browser that cannot remember the attempt simply never auto-reloads, which is
 * the safe direction to fail.
 */
function readAttempted(): boolean {
  try {
    return sessionStorage.getItem(RELOAD_FLAG) === '1';
  } catch {
    return true;
  }
}

function markAttempted(): void {
  try {
    sessionStorage.setItem(RELOAD_FLAG, '1');
  } catch {
    /* no-op: without storage we simply do not retry */
  }
}

function clearAttempted(): void {
  try {
    sessionStorage.removeItem(RELOAD_FLAG);
  } catch {
    /* no-op */
  }
}

/**
 * `React.lazy` that survives a deploy.
 *
 * Vite fingerprints every chunk, so a new build replaces `Foo-abc123.js` with
 * `Foo-def456.js`. A tab that loaded before the deploy still holds the old
 * index, and the next lazy navigation throws "Failed to fetch dynamically
 * imported module" — the app looks broken until the user reloads by hand.
 *
 * VersionWatcher already polls version.json every three minutes, but a person
 * navigating inside that window hits the error first. This closes the gap by
 * reloading for them, once per session: the reload pulls the new index and the
 * route resolves. The session flag means a chunk that is genuinely missing
 * surfaces as a real error instead of reloading forever.
 */
// `any` mirrors React.lazy's own constraint; narrowing it rejects every
// component that takes props.
export function lazyWithReload<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>,
) {
  return lazy(async () => {
    try {
      const loaded = await factory();
      // A successful load means we are on a consistent build again.
      clearAttempted();
      return loaded;
    } catch (error) {
      if (readAttempted()) throw error;

      markAttempted();
      window.location.reload();

      // The reload replaces the document, so this promise deliberately never
      // settles — resolving would render a component we could not fetch.
      return new Promise<{ default: T }>(() => {});
    }
  });
}
